import { i18n } from "@tellescope/i18n";
import { hashString } from "./hash-string";

interface BaseItem {
  id: string;
  timestamp: Date;
}

export interface GetEnduserTimelineAISummaryMetadataArgs<T extends BaseItem> {
  enduserId: string;
  enduserName: string;
  timelineItems: T[];
  locale?: string;
}

const hashInput = <T extends BaseItem>(timelineItems: T[]): string =>
  hashString(
    timelineItems
      .map((item) => `${item.id}:${item.timestamp.toISOString()}`)
      .sort()
      .join(",")
  )
    .toString(16)
    .padStart(8, "0");

export const getEnduserTimelineAISummaryMetadata = <T extends BaseItem>({
  enduserId,
  enduserName,
  timelineItems,
  locale = "en",
}: GetEnduserTimelineAISummaryMetadataArgs<T>): { type: string; message: string; prompt: string } => {
  return {
    type: `timeline-summary-${enduserId}-${hashInput(timelineItems)}`,
    prompt: i18n.t(
      "You are a clinical documentation assistant. When given a list of patient timeline events, respond with a brief markdown-formatted summary. Always refer to the patient by name. Start with a short paragraph (2-3 sentences) summarizing the general themes and topics of recent communications (e.g. what the messages, calls, or emails were about at a high level). If there are actionable items or concerns, list them as a short markdown bullet list using bold labels (e.g. **Action needed:** confirm appointment). Do not count or enumerate individual messages, emails, or interactions. Do not include specific patient personal details such as age, address, weight, or medical history. Do not use headers. Placeholder text in messages or emails (e.g. {{variableName}}) is normal template behavior and should never be flagged as an issue or action item. Write only the summary and nothing else.",
      { ns: "ai", lng: locale }
    ),
    message: i18n.t("Patient: {{patientName}}\n\nTimeline:\n{{timelineJSON}}", {
      patientName: enduserName,
      timelineJSON: JSON.stringify(timelineItems),
      lng: locale,
    }),
  };
};
